import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Empty, Row } from 'antd';
import { CallReviewRow, fetchCallReviews } from 'services/api/reviews';

import useOrgEffect from 'hooks/useOrgEffect';
import { PageWrapper } from 'components/ui';
import { Review } from './components';

const PAGE_LIMIT = 10;

const CallReviewsPage: FC = () => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [isPaging, setPaging] = useState(false);
  const [results, setResults] = useState<CallReviewRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useOrgEffect(() => {
    let cancelled = false;
    const isInitial = page === 1;

    const load = async () => {
      try {
        if (isInitial) setLoading(true);
        else setPaging(true);
        const res = await fetchCallReviews({
          page,
          limit: PAGE_LIMIT
        });

        if (cancelled) return;
        const rows = res.data?.data ?? [];
        setTotal(res.data?.total ?? 0);
        setResults(prev => (isInitial ? rows : [...prev, ...rows]));
      } catch {
        // swallow — empty/partial list on failure
      } finally {
        if (!cancelled) {
          if (isInitial) setLoading(false);
          else setPaging(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [page]);

  return (
    <PageWrapper
      title={t('call_reviews.page_title')}
      color="white"
      loading={loading}
    >
      {results.length ? (
        <>
          {results.map(item => (
            <Review
              key={`review-item-${item.id}`}
              appointmentId={String(item.appointment_id)}
              issues={item.issues ?? []}
              review={item.review}
              satisfied={item.satisfied}
              sender={
                item.sender
                  ? {
                      firstName: item.sender.first_name || '',
                      lastName: item.sender.last_name || '',
                      email: item.sender.email ?? ''
                    }
                  : null
              }
              date={item.created_at}
            />
          ))}
          {total > results.length && (
            <Row justify="center">
              <Button
                type="default"
                onClick={() => setPage(prev => prev + 1)}
                loading={isPaging}
              >
                {t('call_reviews.load_more')}
              </Button>
            </Row>
          )}
        </>
      ) : (
        <Empty />
      )}
    </PageWrapper>
  );
};

export default CallReviewsPage;
